import {ChangeEvent, ReactElement, ReactNode, useCallback, useState} from 'react';

import {FormikErrors, FormikProps, useFormik} from 'formik';
import {FormikConfig, FormikValues} from 'formik/dist/types';

import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import {EmpionButton} from '../EmpionButton/EmpionButton';
import {EmpionDatePicker} from '../EmpionDatePicker/EmpionDatePicker';

import {getButtonStyles} from './SimpleFormikForm.styles';
import {FieldType} from './types';
import {getTextFieldStyle} from '../../features/user/styles/getTextFieldStyle';
import {empionGreen, empionOrange} from '../../layouts/theme/EmpionColors';
import {getFieldStyle} from '../../layouts/theme/FieldStyles';

interface Props<Values extends FormikValues> extends FormikConfig<Values> {
  initialValues: Values;
  buttonText?: string | null;
  hideSubmit?: boolean;
  buttonType?: 'primary' | 'secondary';
  buttonFullWidth?: boolean;
  buttonPosition?: 'center' | 'flex-end' | 'flex-start';
  isFormDisabled?: boolean;
  validate?: (values: Values) => FormikErrors<Values>;
  fields: FieldType<keyof Values>[];
  renderBeforeSubmit?: (formik: FormikProps<Values>) => ReactNode;
  submitExtraDisabled?: (values: Values) => boolean;
}

export function SimpleFormikForm<Values extends FormikValues>({
  initialValues,
  buttonType = 'primary',
  buttonText = '',
  buttonFullWidth = true,
  fields,
  isFormDisabled,
  buttonPosition = 'center',
  hideSubmit,
  renderBeforeSubmit,
  submitExtraDisabled,
  ...formikProps
}: Props<Values>) {
  const theme = useTheme();
  const [firstSubmitFired, setFirstSubmitFired] = useState(false);
  const formik = useFormik({
    initialValues,
    validateOnChange: firstSubmitFired,
    validateOnBlur: firstSubmitFired,
    ...formikProps,
  });
  const buttonStyles = getButtonStyles(buttonType);

  const handleFieldHeader = (fieldHeader?: string | ReactElement) => {
    if (!fieldHeader) {
      return null;
    }

    if (typeof fieldHeader === 'string') {
      return (
        <Typography fontSize='20px' fontWeight={700} paddingY='16px'>
          {fieldHeader}
        </Typography>
      );
    }

    return fieldHeader;
  };

  const onFieldChange = useCallback(
    (e: ChangeEvent<any>, field: FieldType<any>) => {
      formik.handleChange(e);
      if (field.onChangeListener) {
        field.onChangeListener(e.target.value);
      }
    },
    [formik]
  );

  const handleSubmit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      if (!firstSubmitFired) {
        setFirstSubmitFired(true);
      }
      formik.handleSubmit(e);
    },
    [formik, firstSubmitFired]
  );

  return (
    <form onSubmit={handleSubmit}>
      {fields.map((field) => {
        switch (field.componentType) {
          case 'text-field':
            return (
              <Grid key={String(field.fieldName)}>
                {handleFieldHeader(field.fieldHeader)}
                <TextField
                  id={field?.id || String(field.fieldName)}
                  type={field.type || 'text'}
                  size={field.size || 'small'}
                  disabled={isFormDisabled}
                  name={String(field.fieldName)}
                  label={field.fieldLabel}
                  placeholder={field.placeholder}
                  onChange={(e: ChangeEvent<any>) => {
                    onFieldChange(e, field);
                  }}
                  value={formik.values[field.fieldName]}
                  error={!!formik.errors[field.fieldName]}
                  helperText={formik.errors[field.fieldName] as string | undefined}
                  onBlur={formik.handleBlur}
                  autoComplete={field?.autoComplete}
                  fullWidth
                  sx={{paddingBottom: '8px', ...getTextFieldStyle(), ...field.sx}}
                  InputLabelProps={{
                    sx: {
                      color: empionGreen[700],
                    },
                  }}
                  inputProps={{'data-testid': field.testId || `form-field-${String(field.fieldName)}`}}
                  InputProps={{
                    sx: {
                      borderColor: empionOrange[5],
                      backgroundColor: empionOrange[7],
                      color: empionGreen[700],
                    },
                  }}
                />
              </Grid>
            );
          case 'radio':
            return (
              <Grid key={String(field.fieldName)}>
                {handleFieldHeader(field.fieldHeader)}
                <RadioGroup
                  row={field?.direction === 'row'}
                  name={String(field.fieldName)}
                  onBlur={formik.handleBlur}
                  value={formik.values[field.fieldName]}
                  sx={{gap: '12px', ...field.sx}}
                  onChange={(e) => onFieldChange(e, field)}
                >
                  {field.options?.map((option) => {
                    return (
                      <FormControlLabel
                        disabled={isFormDisabled}
                        key={option.label}
                        control={
                          <Radio
                            sx={{
                              color: empionOrange[5],
                              '&.Mui-checked': {
                                color: empionGreen[700],
                              },
                            }}
                          />
                        }
                        label={option.label}
                        value={option.value}
                      />
                    );
                  })}
                </RadioGroup>
              </Grid>
            );

          case 'select':
            return (
              <Grid key={String(field.fieldName)}>
                {handleFieldHeader(field.fieldHeader)}
                <TextField
                  select
                  disabled={isFormDisabled}
                  id={field?.id || String(field.fieldName)}
                  type={field.type || 'text'}
                  size={field.size || 'small'}
                  name={String(field.fieldName)}
                  label={field.fieldLabel}
                  onChange={(e: ChangeEvent<any>) => {
                    onFieldChange(e, field);
                  }}
                  value={formik.values[field.fieldName]}
                  error={!!formik.errors[field.fieldName]}
                  helperText={formik.errors[field.fieldName] as string | undefined}
                  onBlur={formik.handleBlur}
                  autoComplete={field?.autoComplete}
                  fullWidth
                  sx={{paddingBottom: '8px', ...getTextFieldStyle(), ...field.sx}}
                  InputLabelProps={{
                    sx: {
                      color: empionGreen[700],
                    },
                  }}
                  inputProps={{'data-testid': field.testId || `form-field-${String(field.fieldName)}`}}
                  InputProps={{
                    sx: {
                      borderColor: empionOrange[3],
                      backgroundColor: empionGreen[4],
                    },
                  }}
                >
                  {field.options.map((option) => (
                    <MenuItem key={option.label} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            );
          case 'date':
            return (
              <Grid key={String(field.fieldName)}>
                {handleFieldHeader(field.fieldHeader)}
                <EmpionDatePicker
                  setFieldValue={formik.setFieldValue}
                  name={String(field.fieldName)}
                  value={formik.values[field.fieldName]}
                  size={field.size || 'small'}
                  disabled={isFormDisabled}
                  sx={{
                    backgroundColor: empionGreen[5],
                    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                      borderColor: empionOrange[5],
                    },
                    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                      borderColor: empionOrange[3],
                    },
                    ...getTextFieldStyle(),
                    ...field.sx,
                  }}
                />
                {!!formik.errors[field.fieldName] && (
                  <Typography variant='body2' color='#f44336' fontSize='0.75rem' marginLeft='14px' marginTop='4px'>
                    {String(formik.errors[field.fieldName])}
                  </Typography>
                )}
              </Grid>
            );
          case 'checkbox':
            return (
              <Grid key={String(field.fieldName)}>
                {handleFieldHeader(field.fieldHeader)}
                <FormControlLabel
                  disabled={isFormDisabled}
                  onChange={(e) => onFieldChange(e, field)}
                  control={
                    <Checkbox
                      sx={{
                        color: empionOrange[5],
                        '&.Mui-checked': {
                          color: empionOrange[3],
                        },
                      }}
                    />
                  }
                  label={String(field.fieldLabel)}
                  name={String(field.fieldName)}
                  value={formik.values[field.fieldName]}
                  sx={field.sx}
                />
              </Grid>
            );
          case 'autocomplete':
            return (
              <Grid key={String(field.fieldName)}>
                {handleFieldHeader(field.fieldHeader)}
                <Autocomplete
                  options={field.options}
                  fullWidth
                  disabled={isFormDisabled}
                  multiple={!!field.multiple}
                  freeSolo={field.freeSolo ?? true}
                  isOptionEqualToValue={(option, value) => {
                    if (typeof value === 'string') {
                      return option.value === value || option.label === value;
                    }

                    return option.value === value?.value;
                  }}
                  value={formik.values[field.fieldName] || (field.multiple ? [] : '')}
                  onChange={(_event, value) => {
                    formik.setFieldValue(String(field.fieldName), value);
                  }}
                  id={field?.id || String(field.fieldName)}
                  size={field.size || 'small'}
                  sx={field.sx}
                  disableClearable={field.disableClearable ?? true}
                  forcePopupIcon={!!field.forcePopupIcon}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      name={field.fieldName as string}
                      sx={{...getFieldStyle(theme), ...field.sx}}
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          borderColor: empionOrange[5],
                          backgroundColor: empionOrange[7],
                        },
                      }}
                      onChange={(e: ChangeEvent<any>) => {
                        if (field.freeSolo ?? true) {
                          onFieldChange(e, field);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      error={!!formik.errors[field.fieldName]}
                      helperText={formik.errors[field.fieldName] as string | undefined}
                      placeholder={field.placeholder || ''}
                      inputProps={{
                        ...params.inputProps,
                        'data-testid': field.testId || `form-field-${String(field.fieldName)}`,
                      }}
                    />
                  )}
                />
              </Grid>
            );
          default:
            return null;
        }
      })}
      {!hideSubmit && (
        <Grid
          container
          justifyContent={buttonFullWidth ? 'center' : buttonPosition}
          alignItems='center'
          sx={{flexWrap: 'wrap', gap: 2, marginTop: '8px'}}
        >
          {renderBeforeSubmit?.(formik)}
          <EmpionButton
            type='submit'
            disabled={!formik.isValid || !!isFormDisabled || !!submitExtraDisabled?.(formik.values)}
            fullWidth={buttonFullWidth}
            color='primary'
            hoverBackgroundColor={buttonStyles.hovered}
            disabledColor={buttonStyles.disabled}
            textColor={buttonStyles.textColor}
            sx={{
              backgroundColor: buttonStyles.fill,
              marginTop: '8px',
              ...(!!buttonStyles?.borderColor && {border: `1px solid ${buttonStyles.borderColor}}`}),
              alignContent: 'flex-end',
            }}
          >
            <Typography>{buttonText}</Typography>
          </EmpionButton>
        </Grid>
      )}
    </form>
  );
}
